import type { WebSocketClient } from '../lib/websocket';
import { agentDebugLog } from '../lib/agentDebugLog';
import { InterviewConnectionLifecycleController } from './interviewConnectionLifecycleController';
import { InterviewResourceCleanupController } from './interviewResourceCleanupController';
import { InterviewResponseMessageController } from './interviewResponseMessageController';
import { InterviewStateMessageController } from './interviewStateMessageController';
import { InterviewSttMessageController } from './interviewSttMessageController';
import { InterviewSttRecoveryController } from './interviewSttRecoveryController';
import { createInterviewConnectPayload } from './interviewWebSocketProtocol';
import { InterviewWebSocketErrorController } from './interviewWebSocketErrorController';
import type { InterviewWebSocketUiBridge } from './interviewWebSocketRuntimeBridge';
import type { SharedRefs, WebSocketCallbacks } from './interviewWebSocketTypes';

export interface InterviewWebSocketAvatarContext {
  avatarId: number;
  interviewId: number | null;
  avatarType: string;
  dubbingVideo?: string;
  gender?: string;
  age?: number;
}

export interface InterviewWebSocketControllerFactoryInput {
  wsClient: WebSocketClient;
  refs: SharedRefs;
  getCallbacks: () => WebSocketCallbacks;
  ui: InterviewWebSocketUiBridge;
  avatar: InterviewWebSocketAvatarContext;
  createSessionId: () => string;
  getInstructionId: () => string | undefined;
  getUserAgent: () => string | undefined;
  isDocumentVisible: () => boolean;
  nowMs: () => number;
  nowDate: () => Date;
  debugLog: (...args: unknown[]) => void;
}

export interface InterviewWebSocketControllers {
  sttRecovery: InterviewSttRecoveryController;
  connectionLifecycle: InterviewConnectionLifecycleController;
  webSocketErrors: InterviewWebSocketErrorController;
  stateMessages: InterviewStateMessageController;
  responseMessages: InterviewResponseMessageController;
  sttMessages: InterviewSttMessageController;
  markSttAlive: () => void;
}

/** 面接WebSocketの各責務を、同じref・最新callback・UI bridgeへ配線する。 */
export function createInterviewWebSocketControllers(
  input: InterviewWebSocketControllerFactoryInput,
): InterviewWebSocketControllers {
  const {
    wsClient,
    refs,
    getCallbacks,
    ui,
    avatar,
    debugLog,
  } = input;

  const resetTranscriptState = (clearInterim: boolean) => {
    refs.sttPreviousInterimTextRef.current = '';
    refs.sttConfirmedTextRef.current = '';
    refs.sttSessionMessageIdRef.current = null;
    if (clearInterim) ui.setCurrentInterimText('');
  };

  const sttRecovery = new InterviewSttRecoveryController({
    isRecording: () => getCallbacks().isRecordingRef.current,
    isStopping: () => getCallbacks().isStoppingRef.current,
    canRestartRecording: () => typeof getCallbacks().restartRecordingAfterReconnect === 'function',
    restartRecording: () => getCallbacks().restartRecordingAfterReconnect?.(),
    invalidateSession: (reason) => {
      refs.sttReadyRef.current = false;
      wsClient.invalidateSessionReady(reason);
      wsClient.requestSessionBindingRefresh(reason);
    },
    resetSttUi: () => {
      resetTranscriptState(true);
      ui.setIsAwaitingResponse(false);
      ui.setIsRecording(false);
      ui.setRecordingTime(0);
    },
    resetRecording: () => getCallbacks().resetRecordingAfterSessionMissing(),
    stopSilenceDetection: () => getCallbacks().stopSilenceDetection(),
    onAttempt: (trigger, attempt) => {
      agentDebugLog('H7', 'useInterviewWebSocket.ts:attemptSttResume', 'stt_auto_resume', {
        sessionId: refs.sessionIdRef.current,
        trigger,
        attempt,
      });
    },
    onAutoResumeStarted: () => ui.toast({
      title: '録音を自動で再開しています',
      description: 'そのまま続けて話してください。',
    }),
    onAutoResumeExhausted: () => ui.toast({
      title: '録音を自動再開できませんでした',
      description: 'お手数ですが、もう一度マイクを押して話してください。',
      variant: 'destructive',
    }),
    onManualRestartRequired: (message) => ui.toast({
      title: '録音をリセットしました',
      description: message?.trim()
        ? message
        : '音声認識セッションが切れたため、もう一度マイクを押してください。',
      variant: 'destructive',
    }),
    onRestartError: (error) => console.warn('[WebSocket] STT auto-resume failed:', error),
    onResetError: (context, error) => {
      const label = context === 'disconnect' ? 'on disconnect' : 'after STT session missing';
      console.warn(`[WebSocket] Failed to reset recording ${label}:`, error);
    },
  });

  const markSttAlive = () => {
    sttRecovery.markSttAlive();
    refs.sttReadyRef.current = true;
  };

  const connectionLifecycle = new InterviewConnectionLifecycleController({
    connect: () => wsClient.connect(),
    createSessionId: input.createSessionId,
    buildConnectPayload: (sessionId) => createInterviewConnectPayload({
      sessionId,
      interviewId: avatar.interviewId,
      avatarId: avatar.avatarId,
      avatarType: avatar.avatarType,
      dubbingVideo: avatar.dubbingVideo,
      gender: avatar.gender,
      age: avatar.age,
      userAgent: input.getUserAgent(),
      instructionId: input.getInstructionId(),
    }),
    sendConnectSession: (payload) => wsClient.send({ type: 'connect_session', payload }),
    registerDisconnect: (handler) => wsClient.onDisconnect(handler),
    registerReconnect: (handler) => wsClient.onReconnect(handler),
    getSessionId: () => refs.sessionIdRef.current,
    setSessionId: (sessionId) => {
      refs.sessionIdRef.current = sessionId;
    },
    setSessionReady: ui.setSessionReady,
    setSttReady: (ready) => {
      refs.sttReadyRef.current = ready;
    },
    setTargetCategories: ui.setTargetCategories,
    setCounterpartPresentation: (presentation) => {
      getCallbacks().setCounterpartPresentation(presentation);
    },
    isRecording: () => getCallbacks().isRecordingRef.current,
    canRestartRecording: () => typeof getCallbacks().restartRecordingAfterReconnect === 'function',
    prepareSttForReconnect: (wasRecording) => sttRecovery.prepareForReconnect(wasRecording),
    resetDisconnectedUi: () => {
      ui.setIsAwaitingResponse(false);
      ui.setIsPlayingResponse(false);
      ui.setIsRecording(false);
      ui.setCurrentVideoUrl(null);
      ui.setCurrentInterimText('');
      getCallbacks().isRecordingRef.current = false;
      getCallbacks().isStoppingRef.current = false;
      getCallbacks().clearResponseClips?.();
      getCallbacks().stopSilenceDetection();
    },
    toast: ui.toast,
    logInitialConnection: (sessionId) => debugLog('[WebSocket] Session connected:', sessionId),
    logDisconnect: (info) => {
      debugLog('[WebSocket] Disconnected, recovering UI state:', info);
      agentDebugLog('H7', 'useInterviewWebSocket.ts:onDisconnect', 'ws_disconnect_recover', {
        sessionId: refs.sessionIdRef.current,
        code: info.code,
        willReconnect: info.willReconnect,
      });
    },
    logReconnect: (sessionId, shouldAutoRestartRecording) => {
      debugLog('[WebSocket] Reconnected, re-sent connect_session:', sessionId, {
        shouldAutoRestartRecording,
      });
      agentDebugLog('H7', 'useInterviewWebSocket.ts:onReconnect', 'ws_reconnect_recover', {
        sessionId,
        shouldAutoRestartRecording,
      });
    },
    onConnectionError: (error) => console.error('[WebSocket] Connection failed:', error),
  });

  const webSocketErrors = new InterviewWebSocketErrorController({
    resetCommonUi: () => {
      ui.setIsAwaitingResponse(false);
      ui.setIsPlayingResponse(false);
      ui.setIsRecording(false);
      ui.setCurrentInterimText('');
    },
    setSessionReady: ui.setSessionReady,
    invalidateSession: (reason) => wsClient.invalidateSessionReady(reason),
    requestSessionBindingRefresh: (reason) => wsClient.requestSessionBindingRefresh(reason),
    resetTranscriptState: () => {
      resetTranscriptState(false);
      ui.setRecordingTime(0);
    },
    isRecordingOrStopping: () => (
      getCallbacks().isRecordingRef.current || getCallbacks().isStoppingRef.current
    ),
    resetRecordingAfterSessionMissing: () => getCallbacks().resetRecordingAfterSessionMissing(),
    stopSilenceDetection: () => getCallbacks().stopSilenceDetection(),
    onInterviewClosedByScoring: () => getCallbacks().onInterviewClosedByScoring?.(),
    toast: ui.toast,
    logError: (code, message) => console.error('[WebSocket] Error:', code, message),
    logResetError: (error) => {
      console.warn('[WebSocket] Failed to reset recording after recoverable session error:', error);
    },
  });

  const stateMessages = new InterviewStateMessageController({
    setFsmState: ui.setFsmState,
    setPatientState: ui.setPatientState,
    attemptSttResume: () => sttRecovery.attemptResume('patient_tracker_initialized'),
    debugLog,
  });

  const responseMessages = new InterviewResponseMessageController({
    nowMs: input.nowMs,
    nowDate: input.nowDate,
    enqueueResponseClip: (data, mime) => getCallbacks().enqueueResponseClip?.(data, mime),
    appendConversation: (entry) => ui.setConversations((previous) => [...previous, entry]),
    setFsmState: ui.setFsmState,
    setPatientState: ui.setPatientState,
    setLastTiming: ui.setLastTiming,
    setCurrentInterimText: ui.setCurrentInterimText,
    setIsAwaitingResponse: ui.setIsAwaitingResponse,
    setIsPlayingResponse: ui.setIsPlayingResponse,
    setIsRecording: ui.setIsRecording,
    setRecordingTime: ui.setRecordingTime,
    debugLog,
  });

  const sttMessages = new InterviewSttMessageController({
    now: input.nowMs,
    isDocumentVisible: input.isDocumentVisible,
    getCurrentRecognitionClientId: () => refs.recognitionClientIdRef.current,
    getPreviousInterimText: () => refs.sttPreviousInterimTextRef.current,
    setPreviousInterimText: (text) => {
      refs.sttPreviousInterimTextRef.current = text;
    },
    getSessionMessageId: () => refs.sttSessionMessageIdRef.current,
    resetTranscriptState: () => resetTranscriptState(true),
    setCurrentInterimText: ui.setCurrentInterimText,
    setIsAwaitingResponse: ui.setIsAwaitingResponse,
    setIsRecording: ui.setIsRecording,
    setRecordingTime: ui.setRecordingTime,
    isRecording: () => getCallbacks().isRecordingRef.current,
    isStopping: () => getCallbacks().isStoppingRef.current,
    stopRecording: (reason) => getCallbacks().handleStopRecording(reason),
    onSttCorrectionDraft: (draft) => getCallbacks().onSttCorrectionDraft?.(draft),
    markSttAlive,
    toast: ui.toast,
    debugLog,
  });

  return {
    sttRecovery,
    connectionLifecycle,
    webSocketErrors,
    stateMessages,
    responseMessages,
    sttMessages,
    markSttAlive,
  };
}

export interface InterviewResourceCleanupFactoryInput {
  wsClient: WebSocketClient;
  refs: SharedRefs;
  getCallbacks: () => WebSocketCallbacks;
  ui: Pick<
    InterviewWebSocketUiBridge,
    'setIsAwaitingResponse' | 'setIsPlayingResponse' | 'setSessionReady'
      | 'setCurrentVideoUrl' | 'setCurrentInterimText'
  >;
  clearStallTimer: () => void;
  removeMobileReconnectHook: () => void;
}

/** unmount時の解放処理も、常に最新のcallback/refへ接続する。 */
export function createInterviewResourceCleanupController(
  input: InterviewResourceCleanupFactoryInput,
): InterviewResourceCleanupController {
  const { wsClient, refs, getCallbacks, ui } = input;
  return new InterviewResourceCleanupController({
    clearStallTimer: input.clearStallTimer,
    isRecording: () => getCallbacks().isRecordingRef.current,
    stopRecording: () => getCallbacks().handleStopRecording('user'),
    resetUi: () => {
      ui.setIsAwaitingResponse(false);
      ui.setIsPlayingResponse(false);
      ui.setSessionReady(false);
      ui.setCurrentVideoUrl(null);
      ui.setCurrentInterimText('');
    },
    stopSilenceDetection: () => getCallbacks().stopSilenceDetection(),
    getAudioStream: () => getCallbacks().audioStreamRef.current,
    clearAudioStream: () => {
      getCallbacks().audioStreamRef.current = null;
    },
    getAudioContext: () => getCallbacks().audioContextRef.current,
    clearAudioContext: () => {
      getCallbacks().audioContextRef.current = null;
    },
    disconnectWebSocket: () => wsClient.disconnect(),
    removeMobileReconnectHook: input.removeMobileReconnectHook,
    clearConnectionReferences: () => {
      refs.wsClientRef.current = null;
      refs.sessionIdRef.current = null;
      getCallbacks().isStoppingRef.current = false;
    },
  });
}
